import { type RefObject, useEffect } from 'react';

import { SCROLL_CONTAINER_TIMEOUT } from '../model';

type UseInvertedScrollBehaviorProps = {
  reversedRowsLength: number;
  wrapperRef: RefObject<HTMLDivElement | null>;
  containerSelector?: string;
};

export const useInvertedScrollBehavior = ({
  containerSelector,
  reversedRowsLength,
  wrapperRef,
}: UseInvertedScrollBehaviorProps) => {
  useEffect(() => {
    const wrapper = wrapperRef.current;

    if (!wrapper) {
      return;
    }

    let scrollContainer: HTMLElement | null = null;
    let touchStartY = 0;

    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];

      if (touch) {
        touchStartY = touch.clientY;
      }
    };

    const handleTouchMove = (e: TouchEvent) => {
      e.preventDefault();
      const touch = e.touches[0];

      if (touch) {
        const currentY = touch.clientY;
        const deltaY = touchStartY - currentY;

        if (scrollContainer) {
          scrollContainer.scrollTop -= deltaY;
        }

        touchStartY = currentY;
      }
    };

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();

      if (scrollContainer) {
        scrollContainer.scrollTop += e.deltaY;
      }
    };

    const findScrollContainer = (selector?: string): HTMLElement | null => {
      if (selector) {
        const containerBySelector = wrapper.querySelector<HTMLElement>(selector);

        if (containerBySelector) {
          const scrollableChild = containerBySelector.querySelector<HTMLElement>('div[style*="overflow"]');

          if (scrollableChild) {
            return scrollableChild;
          }
        }
      }

      const allDivs = wrapper.querySelectorAll<HTMLElement>('div');

      for (const div of Array.from(allDivs)) {
        const style = window.getComputedStyle(div);

        if (
          style.overflow === 'auto' ||
          style.overflowY === 'auto' ||
          style.overflow === 'scroll' ||
          style.overflowY === 'scroll'
        ) {
          return div;
        }
      }

      return null;
    };

    const timeout = setTimeout(() => {
      scrollContainer = findScrollContainer(containerSelector);

      if (!scrollContainer) {
        return;
      }

      scrollContainer.addEventListener('wheel', handleWheel, { passive: false });
      scrollContainer.addEventListener('touchstart', handleTouchStart, { passive: false });
      scrollContainer.addEventListener('touchmove', handleTouchMove, { passive: false });
    }, SCROLL_CONTAINER_TIMEOUT);

    return () => {
      clearTimeout(timeout);

      if (scrollContainer) {
        scrollContainer.removeEventListener('wheel', handleWheel);
        scrollContainer.removeEventListener('touchstart', handleTouchStart);
        scrollContainer.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [wrapperRef, reversedRowsLength, containerSelector]);
};
